'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { enhanceErrorWithContext, decodeReactError, createFriendlyErrorMessage } from '@/utils/react-error-decoder';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Send detailed error to Coralogix RUM
    this.logErrorToRUM(error, errorInfo);
  }

  private logErrorToRUM(error: Error, errorInfo: ErrorInfo) {
    try {
      // Enhanced error logging with React error decoding
      const enhancedError = enhanceErrorWithContext(error, {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
        source: 'error-boundary'
      });

      const decodedMessage = decodeReactError(error.message);
      
      const errorDetails = {
        ...enhancedError,
        decodedMessage,
        reactError: {
          componentStack: errorInfo.componentStack,
          errorMessage: error.message,
          errorStack: error.stack,
          decodedMessage,
        }
      };

      // Log to console for development
      console.group('🚨 Error Boundary - Detailed Error Report');
      console.error('Original Error:', error);
      console.error('Decoded Message:', decodedMessage);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo.componentStack);
      console.error('Full Details:', errorDetails);
      console.groupEnd();

      // Send to Coralogix RUM using the existing analytics class
      try {
        // Use the existing RUM analytics class
        if (typeof window !== 'undefined') {
          import('@/app/rum').then(({ rumAnalytics }) => {
            rumAnalytics.error(decodedMessage, {
              ...errorDetails,
              source: 'error-boundary',
              originalError: error.message,
              errorStack: error.stack
            });
          }).catch(err => console.warn('Failed to load RUM analytics:', err));
        }
      } catch (rumError) {
        console.warn('Failed to send error to RUM analytics:', rumError);
      }

      // Also try to send via custom event for additional tracking
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('clearitty-error', {
          detail: errorDetails
        }));
      }

    } catch (loggingError) {
      console.error('Failed to log error to RUM:', loggingError);
    }
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Something went wrong</h3>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                We've encountered an unexpected error. Our team has been notified and is working on a fix.
              </p>
            </div>

            {this.state.error && (
              <details className="mb-4">
                <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                  Error Details
                </summary>
                <div className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-40">
                  <div className="text-red-600 mb-2 font-medium">
                    {createFriendlyErrorMessage(this.state.error)}
                  </div>
                  {process.env.NODE_ENV === 'development' && (
                    <>
                      <div className="text-gray-500 mb-2 text-xs">
                        Original: {this.state.error.message}
                      </div>
                      <div className="text-gray-600 font-mono text-xs">
                        {this.state.error.stack}
                      </div>
                    </>
                  )}
                </div>
              </details>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Reload Page
              </button>
              <button
                onClick={() => window.history.back()}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
